import React, { useEffect, useState } from 'react';
import Loading from '../../common/Loading/Loading';
import FormSelect from '../../common/FormSelect/FormSelect';
import { SubscriptionPlan } from '../../../types';
import type { StudentSubscriptionPaymentMethod } from '../../../types/subscription';
import { SubscriptionPlanService } from '../../../services';
import { formatCurrency } from '../../../utils';
import { validateCpf } from '../../../utils/validation';
import {
  GATEWAY_PROVIDERS,
  PAYMENT_SYSTEM_LABELS,
  type GatewayProvider,
} from '../../../types/payment';
import {
  ASAAS_SUBSCRIPTION_PAYMENT_METHOD_OPTIONS,
  CYCLE_LABELS,
  STRIPE_SUBSCRIPTION_PAYMENT_METHOD_OPTIONS,
} from './paymentDetails.constants';
import { createPlanSelectFormatters } from '../../common/PlanNameWithProject/PlanNameWithProject';
import { useEscapeToClose } from '../../../hooks/useEscapeToClose';
import ModalSummary from './ModalSummary';
import styles from './CreateSubscriptionModal.module.css';

type SubscriptionGatewayChoice = GatewayProvider | 'pix';

interface CreateSubscriptionModalProps {
  open: boolean;
  loading: boolean;
  defaultGateway: SubscriptionGatewayChoice;
  studentCpf?: string;
  onConfirm: (
    plan: SubscriptionPlan,
    gateway: SubscriptionGatewayChoice,
    paymentMethod: StudentSubscriptionPaymentMethod | null,
  ) => void;
  onCancel: () => void;
}

const CreateSubscriptionModal: React.FC<CreateSubscriptionModalProps> = ({
  open,
  loading,
  defaultGateway,
  studentCpf = '',
  onConfirm,
  onCancel,
}) => {
  const [plans, setPlans] = useState<SubscriptionPlan[]>([]);
  const [plansLoading, setPlansLoading] = useState(false);
  const [selectedPlanId, setSelectedPlanId] = useState('');
  const [selectedGateway, setSelectedGateway] = useState<SubscriptionGatewayChoice>(defaultGateway);
  const [selectedPaymentMethod, setSelectedPaymentMethod] = useState<StudentSubscriptionPaymentMethod>('credit_card');

  useEffect(() => {
    if (!open) {
      setSelectedPlanId('');
      setSelectedPaymentMethod('credit_card');
      return;
    }
    setSelectedGateway(defaultGateway);
    setSelectedPaymentMethod('credit_card');
    setPlansLoading(true);
    SubscriptionPlanService.getSelectablePlans()
      .then(setPlans)
      .finally(() => setPlansLoading(false));
  }, [defaultGateway, open]);

  useEscapeToClose(onCancel, open && !loading);

  if (!open) return null;

  const selectedPlan = plans.find((p) => String(p.id) === selectedPlanId) ?? null;
  const selectedPlanCycle = selectedPlan?.billing_cycle.trim().toLowerCase() ?? '';
  const selectedPlanCycleLabel = selectedPlan
    ? CYCLE_LABELS[selectedPlanCycle] ?? CYCLE_LABELS[selectedPlan.billing_cycle] ?? selectedPlan.billing_cycle
    : '';
  const selectedPlanIsSinglePayment = Boolean(selectedPlan?.is_single_payment)
    || (selectedPlanCycle !== '' && selectedPlanCycle !== 'monthly');

  const planOptions = plans.map((p) => ({ value: String(p.id), label: p.name }));
  const planSelectFormatters = createPlanSelectFormatters(plans);
  const gatewayOptions = [
    ...GATEWAY_PROVIDERS.map((gateway) => ({
      value: gateway,
      label: PAYMENT_SYSTEM_LABELS[gateway],
    })),
    { value: 'pix', label: PAYMENT_SYSTEM_LABELS.pix },
  ];

  const showsAsaasPaymentMethod = selectedGateway === 'asaas';
  const showsStripePaymentMethod = selectedGateway === 'stripe';
  const showsPaymentMethod = showsAsaasPaymentMethod || showsStripePaymentMethod;
  const asaasCpfError = selectedGateway === 'asaas'
    ? validateCpf(studentCpf, { required: true })
    : '';
  const paymentMethodValid = !showsPaymentMethod
    || (showsAsaasPaymentMethod
      && (selectedPaymentMethod === 'pix' || selectedPaymentMethod === 'credit_card'))
    || (showsStripePaymentMethod && selectedPaymentMethod === 'credit_card');
  const canConfirm = Boolean(selectedPlanId)
    && !plansLoading
    && !asaasCpfError
    && paymentMethodValid;

  const resolvedPaymentMethod: StudentSubscriptionPaymentMethod | null = showsStripePaymentMethod
    ? 'credit_card'
    : showsAsaasPaymentMethod
      && (selectedPaymentMethod === 'pix' || selectedPaymentMethod === 'credit_card')
      ? selectedPaymentMethod
      : null;

  return (
    <div className={styles.overlay}>
      <div className={styles.modal} onClick={(e) => e.stopPropagation()}>
        <div className={styles['icon-circle']}>
          <span className="material-symbols-outlined">autorenew</span>
        </div>

        <h3 className={styles.title}>Criar assinatura</h3>
        <p className={styles.message}>Selecione o gateway e o plano de assinatura para esta aluna.</p>

        <div className={styles['select-wrap']}>
          <FormSelect
            id="subscription-gateway"
            label="Gateway"
            options={gatewayOptions}
            value={selectedGateway}
            onChange={(e) => {
              const nextGateway = e.target.value as SubscriptionGatewayChoice;
              setSelectedGateway(nextGateway);
              if (nextGateway === 'asaas' || nextGateway === 'stripe') {
                setSelectedPaymentMethod('credit_card');
              }
            }}
            disabled={loading}
          />
        </div>

        {showsAsaasPaymentMethod && (
          <div className={styles['select-wrap']}>
            <FormSelect
              id="subscription-payment-method"
              label="Forma de pagamento"
              options={ASAAS_SUBSCRIPTION_PAYMENT_METHOD_OPTIONS}
              value={selectedPaymentMethod}
              onChange={(e) => {
                const value = e.target.value;
                setSelectedPaymentMethod(
                  value === 'pix' || value === 'credit_card' ? value : 'credit_card',
                );
              }}
              disabled={loading}
            />
          </div>
        )}

        {showsStripePaymentMethod && (
          <div className={styles['select-wrap']}>
            <FormSelect
              id="subscription-payment-method-stripe"
              label="Forma de pagamento"
              options={STRIPE_SUBSCRIPTION_PAYMENT_METHOD_OPTIONS}
              value="credit_card"
              onChange={() => {
                setSelectedPaymentMethod('credit_card');
              }}
              disabled={loading}
              readOnly
            />
          </div>
        )}

        <div className={styles['select-wrap']}>
          {plansLoading ? (
            <Loading />
          ) : (
            <FormSelect
              id="subscription-plan"
              label="Plano de assinatura"
              options={planOptions}
              value={selectedPlanId}
              onChange={(e) => setSelectedPlanId(e.target.value)}
              disabled={loading}
              triggerFormat={planSelectFormatters.triggerFormat}
              optionFormat={planSelectFormatters.optionFormat}
            />
          )}
        </div>

        {asaasCpfError && (
          <p className={styles.validation}>
            CPF obrigatório para criar assinatura no Asaas: <strong>{asaasCpfError}</strong>
            {' '}Atualize o cadastro da aluna antes de continuar.
          </p>
        )}

        {selectedPlan && (
          <ModalSummary
            items={[
              {
                label: 'Valor',
                value: `${formatCurrency(selectedPlan.price_cents / 100)}/${selectedPlanCycleLabel.toLowerCase() || 'mês'}`,
              },
              { label: 'Ciclo', value: selectedPlanCycleLabel },
              { label: 'À Vista', value: selectedPlanIsSinglePayment ? 'Sim' : 'Não' },
              {
                label: 'Fidelidade',
                value: selectedPlan.loyalty_months > 0
                  ? `${selectedPlan.loyalty_months} ${selectedPlan.loyalty_months === 1 ? 'mês' : 'meses'}`
                  : 'Sem fidelidade',
              },
              ...(String(selectedPlan.billing_cycle).toLowerCase() === 'yearly'
                ? [{
                    label: 'Renovação',
                    value: selectedPlan.auto_renew ? 'Automática' : 'Não automática',
                  }]
                : []),
            ]}
          />
        )}

        <div className={styles.actions}>
          <button
            type="button"
            className={`${styles.btn} ${styles['btn-cancel']}`}
            onClick={onCancel}
            disabled={loading}
          >
            Cancelar
          </button>
          <button
            type="button"
            className={`${styles.btn} ${styles['btn-confirm']}`}
            onClick={() => selectedPlan && canConfirm && onConfirm(
              selectedPlan,
              selectedGateway,
              resolvedPaymentMethod,
            )}
            disabled={loading || !canConfirm}
          >
            {loading ? 'Aguarde...' : 'Confirmar'}
          </button>
        </div>
      </div>
    </div>
  );
};

export default CreateSubscriptionModal;
